import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Upload, ArrowLeft } from "lucide-react";
import { createTransferAllowDuplicates } from "@/lib/transfersApi";

interface JsonTransfer {
  id: number;
  transfer_code?: string;
  emirate?: string;
  title?: string;
  description?: string;
  type?: string;
  capacity?: number | string;
  price_op?: number | string;
  price_ag?: number | string;
  price_cf?: number | string;
  supplier_name?: string;
  cost_supplier?: number | string;
  tax_percentage?: number | string;
  hcte_notes?: string;
  pricing_type?: string;
  image_url?: string;
}

interface ImportResults {
  success: number;
  skipped: number;
  errors: string[];
  reasonCounts: Record<string, number>;
}

const ALLOWED_TRANSFER_TYPES = ["driver", "guide", "staff", "luggage_car"] as const;
const ALLOWED_PRICING_TYPES = ["per_person", "per_family", "per_group"] as const;

const ImportTransfers = () => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<ImportResults | null>(null);
  const { userRole } = useAuth();
  const navigate = useNavigate();

  const canImport = userRole === "ceo" || userRole === "general_manager";

  if (!canImport) {
    return (
      <DashboardLayout>
        <Alert>
          <AlertDescription>
            You don't have permission to import transfers. Only CEO and General Manager can perform this action.
          </AlertDescription>
        </Alert>
      </DashboardLayout>
    );
  }

  const cleanValue = (value: unknown): string | null => {
    if (!value || value === "" || value === "N/A" || value === "null" || value === "undefined") {
      return null;
    }
    return String(value).trim();
  };

  const parseNumber = (value: unknown): number | null => {
    if (!value || value === "" || value === "N/A" || value === "null" || value === "undefined") {
      return null;
    }
    const num = parseFloat(String(value).replace(/,/g, ""));
    return isNaN(num) ? null : num;
  };

  const validatePricingType = (value: unknown): "per_person" | "per_family" | "per_group" => {
    const cleaned = cleanValue(value)?.toLowerCase();
    if (cleaned === "per_person" || cleaned === "per_family" || cleaned === "per_group") {
      return cleaned as "per_person" | "per_family" | "per_group";
    }
    return "per_group";
  };

  const validateTransferType = (value: unknown): "driver" | "guide" | "staff" | "luggage_car" | null => {
    const cleaned = cleanValue(value)?.toLowerCase();
    if (cleaned === "luggage") {
      return "luggage_car";
    }
    if (cleaned === "driver" || cleaned === "guide" || cleaned === "staff" || cleaned === "luggage_car") {
      return cleaned;
    }
    return null;
  };

  const incrementReason = (reasonCounts: Record<string, number>, reason: string) => {
    reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
  };

  const formatApiError = (error: unknown): { message: string; reason: string } => {
    const fallback = { message: "Unknown API error", reason: "Unknown API error" };
    if (!error || typeof error !== "object") return fallback;

    const payload = error as {
      message?: string;
      code?: string;
      status?: number;
      errors?: Record<string, string>;
    };

    const fieldErrors = payload.errors
      ? Object.entries(payload.errors).map(([field, message]) => `${field}: ${message}`)
      : [];

    const parts = [
      payload.message?.trim(),
      payload.code ? `code=${payload.code}` : null,
      payload.status ? `status=${payload.status}` : null,
      fieldErrors.length > 0 ? `fields=[${fieldErrors.join("; ")}]` : null,
    ].filter(Boolean) as string[];

    const message = parts.length > 0 ? parts.join(" | ") : fallback.message;
    return { message, reason: payload.message?.trim() || fallback.reason };
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith(".json")) {
        toast.error("Please select a JSON file");
        return;
      }
      setFile(selectedFile);
      setResults(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    setImporting(true);
    setProgress(0);
    const errors: string[] = [];
    const reasonCounts: Record<string, number> = {};
    let successCount = 0;
    let skippedCount = 0;

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const data: JsonTransfer[] = Array.isArray(parsed)
        ? parsed
        : (parsed?.transfers || parsed?.items || parsed?.data || parsed?.services || []);

      if (!Array.isArray(data)) {
        throw new Error("JSON file must contain an array of transfers");
      }

      const total = data.length;
      console.info("[Transfer Import] Starting import", {
        fileName: file.name,
        totalRows: total,
        allowedTransferTypes: ALLOWED_TRANSFER_TYPES,
        allowedPricingTypes: ALLOWED_PRICING_TYPES,
      });

      for (let i = 0; i < total; i++) {
        const item = data[i];
        setProgress(Math.round(((i + 1) / total) * 100));

        try {
          const rawType = cleanValue(item.type)?.toLowerCase() || null;
          const rawPricingType = cleanValue(item.pricing_type)?.toLowerCase() || null;

          const transferData = {
            id: item.id,
            transfer_code: cleanValue(item.transfer_code),
            emirate: cleanValue(item.emirate),
            title: cleanValue(item.title),
            description: cleanValue(item.description),
            type: validateTransferType(item.type),
            capacity: parseNumber(item.capacity),
            price_op: parseNumber(item.price_op),
            price_ag: parseNumber(item.price_ag),
            price_cf: parseNumber(item.price_cf),
            supplier_name: cleanValue(item.supplier_name),
            cost_supplier: parseNumber(item.cost_supplier),
            tax_percentage: parseNumber(item.tax_percentage),
            hcte_notes: cleanValue(item.hcte_notes),
            pricing_type: validatePricingType(item.pricing_type),
            image_url: cleanValue(item.image_url),
          };

          const blockingIssues: string[] = [];
          if (!transferData.transfer_code) blockingIssues.push("missing transfer_code");
          if (!transferData.emirate) blockingIssues.push("missing emirate");
          if (!transferData.title) blockingIssues.push("missing title");
          if (!transferData.type) {
            blockingIssues.push(
              `invalid type "${rawType ?? "<empty>"}" (allowed: ${ALLOWED_TRANSFER_TYPES.join(", ")})`
            );
          }

          if (blockingIssues.length > 0) {
            const reason = `Pre-validation failed: ${blockingIssues.join("; ")}`;
            errors.push(
              `Row ${i + 1} (${transferData.transfer_code || "no-code"}): ${reason}`
            );
            skippedCount++;
            incrementReason(reasonCounts, "Pre-validation failed");
            console.warn("[Transfer Import] Skipped row before API call", {
              row: i + 1,
              reason,
              rawType,
              rawPricingType,
              rawItem: item,
              normalizedPayload: transferData,
            });
            continue;
          }

          if (rawPricingType && rawPricingType !== transferData.pricing_type) {
            console.info("[Transfer Import] pricing_type normalized", {
              row: i + 1,
              transferCode: transferData.transfer_code,
              from: rawPricingType,
              to: transferData.pricing_type,
            });
          }
          if (rawType && transferData.type && rawType !== transferData.type) {
            console.info("[Transfer Import] type normalized", {
              row: i + 1,
              transferCode: transferData.transfer_code,
              from: rawType,
              to: transferData.type,
            });
          }

          let created = false;
          try {
            await createTransferAllowDuplicates(transferData);
            created = true;
          } catch (error: unknown) {
            const apiError = formatApiError(error);
            errors.push(
              `Row ${i + 1} (${transferData.transfer_code || "no-code"}): ${apiError.message}`
            );
            skippedCount++;
            incrementReason(reasonCounts, apiError.reason);
            console.error("[Transfer Import] API error on row", {
              row: i + 1,
              transferCode: transferData.transfer_code,
              title: transferData.title,
              rawType,
              rawPricingType,
              normalizedPayload: transferData,
              apiError,
              error,
            });
          }

          if (created) {
            successCount++;
          }
        } catch (error: unknown) {
          const processingMessage =
            error && typeof error === "object" && "message" in error
              ? String((error as { message?: string }).message || "Unknown processing error")
              : "Unknown processing error";
          errors.push(`Row ${i + 1}: ${processingMessage}`);
          skippedCount++;
          incrementReason(reasonCounts, "Processing error");
          console.error("[Transfer Import] Processing error", { row: i + 1, rawItem: item, error });
        }
      }

      console.info("[Transfer Import] Completed import", {
        fileName: file.name,
        totalRows: total,
        successCount,
        skippedCount,
        reasonCounts,
      });

      setResults({ success: successCount, skipped: skippedCount, errors, reasonCounts });

      if (successCount > 0) {
        toast.success(`Successfully imported ${successCount} transfers`);
      }
      if (skippedCount > 0) {
        toast.warning(`Skipped ${skippedCount} transfers due to errors`);
      }
    } catch (error: unknown) {
      const processingMessage =
        error && typeof error === "object" && "message" in error
          ? String((error as { message?: string }).message || "Unknown file processing error")
          : "Unknown file processing error";
      console.error("[Transfer Import] File processing error", { error });
      toast.error(`Failed to process file: ${processingMessage}`);
      setResults({ success: 0, skipped: 0, errors: [processingMessage], reasonCounts });
    } finally {
      setImporting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate("/transfers")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Transfers
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Import Transfers</h1>
            <p className="text-muted-foreground">Upload a JSON file to import transfers in bulk</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Upload JSON File</CardTitle>
            <CardDescription>
              Select a JSON file containing an array of transfer objects to import. Requires CEO or General Manager role (same as in database <code>user_roles</code>).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <Input
                type="file"
                accept=".json"
                onChange={handleFileChange}
                disabled={importing}
                className="flex-1"
              />
              <Button onClick={handleImport} disabled={!file || importing} className="gap-2">
                <Upload className="h-4 w-4" />
                {importing ? "Importing..." : "Import Transfers"}
              </Button>
            </div>

            {file && (
              <p className="text-sm text-muted-foreground">
                Selected file: <span className="font-medium">{file.name}</span>
              </p>
            )}

            {importing && (
              <div className="space-y-2">
                <Progress value={progress} />
                <p className="text-sm text-center text-muted-foreground">{progress}% complete</p>
              </div>
            )}

            {results && (
              <Alert>
                <AlertDescription>
                  <div className="space-y-2">
                    <p className="font-medium">Import Results:</p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li className="text-green-600">Successfully imported: {results.success}</li>
                      {results.skipped > 0 && (
                        <li className="text-yellow-600">Skipped: {results.skipped}</li>
                      )}
                    </ul>
                    {Object.keys(results.reasonCounts).length > 0 && (
                      <div className="mt-3">
                        <p className="font-medium mb-1">Top skip reasons:</p>
                        <ul className="list-disc list-inside space-y-1 text-xs text-muted-foreground">
                          {Object.entries(results.reasonCounts)
                            .sort((a, b) => b[1] - a[1])
                            .slice(0, 8)
                            .map(([reason, count]) => (
                              <li key={reason}>
                                {reason}: {count}
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                    {results.errors.length > 0 && (
                      <div className="mt-4">
                        <p className="font-medium text-red-600 mb-2">Errors:</p>
                        <div className="max-h-40 overflow-y-auto text-xs space-y-1">
                          {results.errors.slice(0, 20).map((error, idx) => (
                            <p key={idx} className="text-red-600">
                              {error}
                            </p>
                          ))}
                          {results.errors.length > 20 && (
                            <p className="text-muted-foreground italic">
                              ... and {results.errors.length - 20} more errors
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ImportTransfers;
